import React, { useEffect, useState } from 'react';
import { Alert, App, Button, Space, Table, Tooltip } from 'antd';

import { useAccess } from '../../../auth/AccessContext.jsx';
import ActionIcon from '../../../components/ActionIcon.jsx';
import { formatInstant } from '../../../format';
import ActionConfirmModal from './ActionConfirmModal.jsx';
import { QUEUE_OMITS, orderColumns, reviewedByColumn, reviewsColumn } from './orderColumns.jsx';
import { selectionProblem, withPartners } from './orders.js';
import ReasonModal from './ReasonModal.jsx';

/**
 * To approve: every READY order reviewed in its current cycle that the
 * reader reviewed in NO cycle (GET /orders ?queue=APPROVE; the server leaves
 * out every order the reader ever reviewed, G-6 and IP-128). The checker
 * approves selected orders or all of them - approval allocates the units at
 * the dealing date's price and posts them (IP-62) - or answers otherwise:
 * Send back to the makers for a new review (IP-85: the review cycle ends), or
 * Reject with a reason (IP-86). Each row says who reviewed it and how many
 * reviews its cycle has (design 8.5), and a subscription's entry fee with
 * its rate, read-only (IP-136).
 *
 * An order whose dealing date the pricing guard holds is left out of an
 * approval: the server would refuse the whole request for it (PRICING_GUARD).
 *
 * The approve buttons follow the screen's 'approve', which is ORDER_APPROVE's
 * claim alone (core.order.approve); a cash checker's approval of a statement
 * match is 'approveMatch' (@lfoc/domain/permissions.js).
 *
 * @param {{ list: object, blocked: Map<string, string[]>, onOpen: (order) => void,
 *           onActed: (kind, result) => void, onRefresh: () => void }} props
 */
const ApproveQueue = ({ list, blocked, onOpen, onActed, onRefresh }) => {
    const { canAct } = useAccess();
    const { message } = App.useApp();
    const canApprove = canAct('approval', 'approve');
    const canSendBack = canAct('approval', 'sendBack');
    const canReject = canAct('approval', 'reject');
    const [selected, setSelected] = useState([]);
    const [confirming, setConfirming] = useState(null); // { kind: 'approve'|'reject', orders }
    const [sendBack, setSendBack] = useState(null); // { orders }

    useEffect(() => {
        setSelected((current) => current.filter((id) => list.orders.some((o) => o.orderId === id)));
    }, [list.orders]);

    const held = (o) => blocked.has(o.dealingDate);
    const picked = withPartners(selected, list.orders);
    const pickedHeld = picked.orders.filter(held);
    const everyone = withPartners(list.orders.map((o) => o.orderId), list.orders).orders.filter((o) => !held(o));

    const confirm = (kind, orders) => {
        const problem = selectionProblem(kind, orders);
        if (problem) {
            message.warning(problem);
            return;
        }
        setConfirming({ kind, orders });
    };

    const sendBackForReview = () => {
        const problem = selectionProblem('sendBack', picked.orders);
        if (problem) {
            message.warning(problem);
            return;
        }
        setSendBack({ orders: picked.orders });
    };

    const doneConfirm = (kind, result) => {
        setConfirming(null);
        setSelected([]);
        onActed(kind, result);
    };

    const doneSendBack = (kind, result) => {
        setSendBack(null);
        setSelected([]);
        onActed(kind, result);
    };

    const approveReason = !canApprove
        ? 'Your roles cannot approve transactions'
        : pickedHeld.length
            ? 'No published price for a dealing date in the selection; approve the others'
            : null;

    const columns = [
        ...orderColumns({ onOpen, blocked, omit: QUEUE_OMITS }),
        reviewedByColumn(),
        reviewsColumn(),
        { title: 'Last reviewed', dataIndex: 'reviewedAt', key: 'reviewedAt', width: 170, render: formatInstant }
    ];

    return (
        <>
            <Space className="ready-approval-actions" wrap>
                {canSendBack && (
                    <Button className="ready-action-button" disabled={!selected.length} onClick={sendBackForReview}>Send back</Button>
                )}
                {canReject && (
                    <Button className="ready-action-button" danger disabled={!selected.length}
                        onClick={() => confirm('reject', list.orders.filter((o) => selected.includes(o.orderId)))}>
                        Reject
                    </Button>
                )}
                <Tooltip title={approveReason}>
                    <span>
                        <Button className="ready-action-button" type="primary" disabled={Boolean(approveReason) || !picked.orders.length}
                            onClick={() => confirm('approve', picked.orders)}>
                            Approve selected ({picked.orders.length})
                        </Button>
                    </span>
                </Tooltip>
                <Tooltip title={canApprove ? null : 'Your roles cannot approve transactions'}>
                    <span>
                        <Button className="ready-action-button" disabled={!canApprove || !everyone.length}
                            onClick={() => confirm('approve', everyone)}>
                            Approve all ({everyone.length})
                        </Button>
                    </span>
                </Tooltip>
                <Button className="refresh-button" icon={<ActionIcon name="refresh" />} onClick={onRefresh} loading={list.loading}>
                    Refresh
                </Button>
            </Space>

            {picked.incomplete.length > 0 && (
                <Alert
                    type="warning"
                    showIcon
                    className="auth-alert"
                    title={'The other leg of ' + picked.incomplete.map((o) => o.transactionRef).join(', ') + ' is not waiting for approval yet. A switch or transfer is approved whole, so approving it now will be skipped.'}
                />
            )}
            {list.nextCursor && (
                <Alert type="info" showIcon className="auth-alert" title={'Showing the first ' + list.orders.length + ' transactions. Approve these, then refresh for the rest.'} />
            )}

            <Table
                className="approval-orders-table"
                rowKey="orderId"
                size="small"
                loading={list.loading}
                columns={columns}
                dataSource={list.loading && list.orders.length === 0 ? undefined : list.orders}
                pagination={{ pageSize: 20, hideOnSinglePage: true }}
                locale={{ emptyText: 'Nothing to approve - a transaction arrives here once it is ready and someone else has reviewed it' }}
                scroll={{ x: 'max-content' }}
                rowSelection={canApprove || canSendBack || canReject ? { selectedRowKeys: selected, onChange: setSelected } : undefined}
            />

            <ActionConfirmModal
                kind={confirming?.kind ?? null}
                orders={confirming?.orders ?? []}
                onClose={() => setConfirming(null)}
                onDone={doneConfirm}
            />
            <ReasonModal
                kind={sendBack ? 'sendBack' : null}
                orders={sendBack?.orders ?? []}
                onClose={() => setSendBack(null)}
                onDone={doneSendBack}
            />
        </>
    );
};

export default ApproveQueue;
